import { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  Area,
  AreaChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ComposedChart,
} from "recharts";
import api from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";
import PageHeader from "@/components/PageHeader";
import StatBlock from "@/components/StatBlock";
import { fmtKg, fmtNum } from "@/lib/format";

export default function Forecast() {
  const { active } = useProjects();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!active) return;
    api.get(`/projects/${active.id}/forecast?months_ahead=12`).then((r) => setData(r.data));
  }, [active]);

  if (!active) return <div className="p-12 theme-ink-muted">Select a project first.</div>;

  const combined = [
    ...(data?.history || []).map((h) => ({ month: h.month, actual_t: (h.total || 0) / 1000 })),
    ...(data?.forecast || []).map((f) => ({ month: f.month, predicted_t: f.predicted_kg / 1000 })),
  ];

  return (
    <div className="animate-fade-up">
      <PageHeader
        overline="Module · Predictive Engine"
        title="Carbon Forecast"
        subtitle="AI-assisted forecast of upcoming monthly emissions and a Net-Zero trajectory to 2050."
        guideId="forecast"
      />

      <section className="px-8 pt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatBlock label="Annual run-rate" value={fmtKg(data?.annual_run_rate_kg)} accent="gold" />
        <StatBlock label="Monthly trend (slope)" value={fmtNum(data?.slope_kg_per_month, 0)} unit="kg/mo" accent={(data?.slope_kg_per_month || 0) < 0 ? "eco" : "risk"} />
        <StatBlock label="Forecast horizon" value="12 mo" accent="white" />
        <StatBlock label="Target year" value="2050" unit="Net Zero" accent="sky" />
      </section>

      <section className="px-8 pt-6 grid grid-cols-1 xl:grid-cols-2 gap-4 pb-12">
        <div className="border border theme-border theme-surface p-6">
          <div className="overline">Actual vs Forecast (tCO₂e)</div>
          <div className="font-serif-display text-2xl">12-month projection</div>
          <div className="h-80 mt-3">
            <ResponsiveContainer>
              <ComposedChart data={combined}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="month" stroke="#71717A" />
                <YAxis stroke="#71717A" />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="actual_t" name="Actual" stroke="#38BDF8" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="predicted_t" name="Forecast" stroke="#D4AF37" strokeWidth={2} strokeDasharray="4 4" dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="border border theme-border theme-surface p-6">
          <div className="overline">Net-Zero trajectory</div>
          <div className="font-serif-display text-2xl">Annualised path to 2050</div>
          <div className="h-80 mt-3">
            <ResponsiveContainer>
              <AreaChart data={(data?.trajectory_to_target || []).map((t) => ({ year: t.year, kt: t.kg / 1_000_000 }))}>
                <defs>
                  <linearGradient id="eco" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#4ADE80" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#4ADE80" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="year" stroke="#71717A" />
                <YAxis stroke="#71717A" />
                <Tooltip />
                <Area type="monotone" dataKey="kt" stroke="#4ADE80" fill="url(#eco)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>
    </div>
  );
}
